import React from "react";
import Icon from "./Icon";
import KebabMenu from "../../../components/KebabMenu";
import { pluralize } from "./checklistModel";

function PublishControl({ published, canToggle, loading, onToggle }) {
  const label = published ? "Published" : "Not published";
  if (!canToggle) return <span className={`pub static${published ? "" : " off"}`}>{label}</span>;
  return (
    <button type="button" className="pub" role="switch" aria-checked={published} aria-label="Published to students"
      disabled={loading} onClick={onToggle}>
      <span className="sw" />
      <span className={published ? "" : "off"}>{label}</span>
    </button>
  );
}

function Progress({ model }) {
  const { published, completed, enrolled, pct } = model;
  return (
    <div className="prog" aria-label={`${completed} of ${enrolled} students complete`}>
      <div className="top">
        <b>{published ? "Published" : "Not published"}</b>
        <em>{completed} of {pluralize(enrolled, "student")}</em>
      </div>
      <div className="bar"><i style={{ width: `${Math.max(pct, 2)}%` }} /></div>
      <div className="nx">{completed ? "Assessments in progress" : "No completions yet"}</div>
    </div>
  );
}

function Detail({ model, canEdit, onConfigure, onReport, id }) {
  return (
    <div className="det" id={id}>
      <div className={`det-in${model.kind === "bank" ? " one" : ""}`}>
        <div>
          <div className="dh">
            <h4>Rubric{model.description ? ` · ${model.description}` : ""}</h4>
            {canEdit && onConfigure && <button type="button" className="lnk" onClick={onConfigure}>Configure</button>}
          </div>
          {model.skills.length === 0 ? <p className="note">No skills yet.</p> : (
            <ol className="rubric">
              {model.skills.map((s, i) => (
                <li key={i}>
                  {s.critical && <span className="crit" title="Critical step"><Icon n="alert" s={13} /></span>}
                  {s.label}
                </li>
              ))}
            </ol>
          )}
          <p className="note">
            {model.required ? `Pass: ${model.required} skills (${model.threshold})` : "No pass threshold"}
          </p>
        </div>
        {model.kind === "checklist" && (
        <div>
          <div className="dh">
            <h4>Student completion</h4>
            {onReport && <button type="button" className="lnk" onClick={onReport}>Completion report</button>}
          </div>
          {model.students.length === 0 ? (
            <p className="note">{model.published ? "No attempts yet." : "Publish to begin tracking."}</p>
          ) : (
            <ul className="st">
              {model.students.map((s) => (
                <li key={s.id}>
                  <span className={`a${s.done ? " done" : ""}`}>{s.done ? <Icon n="check" s={13} w={2.5} /> : s.initials}</span>
                  <span>{s.name}{s.peerApproved && <small>Peer completion confirmed</small>}</span>
                  <em className="mono">{s.label}</em>
                </li>
              ))}
            </ul>
          )}
          {!model.published && <p className="note">Students can&apos;t see this checklist until it&apos;s published.</p>}
        </div>
        )}
      </div>
    </div>
  );
}

/**
 * One checklist or bank sheet as a row: title and rubric summary, completion, publish switch and actions, with the
 * rubric and student completion in an expandable detail. `menuItems` are the overflow actions ({ icon, label, danger,
 * onSelect }); `titleSlot` replaces the title/description while a bank sheet is being renamed.
 */
export default function ChecklistRow({
  model, canEdit, expanded, onExpand, publishing, onTogglePublish, onAssess, onReport, onConfigure, menuItems,
  titleSlot, extraAction, reorder, dragProps, dragging, dropOver,
}) {
  const detailId = `ck-detail-${model.kind}-${model.id}`;
  const showProgress = model.kind === "checklist";
  const cls = ["ck", expanded ? "open" : "", dragging ? "dragging" : "", dropOver ? "drop" : "", model.published ? "" : "unpub"]
    .filter(Boolean).join(" ");
  return (
    <div className={cls} data-testid={`checklist-row-${model.kind}-${model.id}`} {...dragProps}>
      <div className="row">
        <button type="button" className="tg" aria-expanded={expanded} aria-controls={detailId} onClick={onExpand}
          aria-label={`${expanded ? "Hide" : "Show"} details for ${model.title}`}>
          <Icon n="chev" s={16} />
        </button>

        <div className="main">
          {titleSlot || (
            <>
              <h3 className="title" title={model.title}>{model.title}</h3>
              {model.description && <p className="desc">{model.description}</p>}
            </>
          )}
          <div className="meta">
            {!model.published && <span className="hid"><Icon n="eyeOff" />Hidden from students</span>}
            {model.fromBank && <span className="chip">From Bank</span>}
            {model.requiredForPass && <span className="chip warn">Required for pass</span>}
            {model.inLearningPlan && <span className="chip lock"><Icon n="lock" s={12} />In Learning Plan</span>}
            {model.peerAssessmentRequired && <span className="chip"><Icon n="users" s={12} />Peer practice required</span>}
            <span><Icon n="list" />{pluralize(model.skillCount, "skill")} · Pass/Fail per skill</span>
            {model.kind === "checklist" && <span><Icon n="user" />Instructor evaluates</span>}
            {model.required ? <span>Pass: {model.required}</span> : <span className="mu">No pass threshold</span>}
            <span className="mu"><Icon n="clock" />Updated {model.updated}</span>
          </div>
        </div>

        {showProgress ? <Progress model={model} /> : <div className="prog" />}

        <div className="acts">
          {reorder && (
            <span className="reorder">
              <button type="button" aria-label={`Move ${model.title} up`} disabled={reorder.upDisabled} onClick={reorder.onUp}><Icon n="up" s={13} /></button>
              <button type="button" aria-label={`Move ${model.title} down`} disabled={reorder.downDisabled} onClick={reorder.onDown}><Icon n="down" s={13} /></button>
            </span>
          )}
          {extraAction}
          {onAssess && model.published && (
            <button type="button" className="btn btn-p" onClick={onAssess}><Icon n="clip" />Conduct assessment</button>
          )}
          {!model.published && canEdit && (
            <button type="button" className="btn btn-o" disabled={publishing} onClick={onTogglePublish}><Icon n="eye" />Publish</button>
          )}
          {model.published && <PublishControl published canToggle={canEdit} loading={publishing} onToggle={onTogglePublish} />}
          <KebabMenu label={`More actions for ${model.title}`} triggerIcon={<Icon n="more" s={16} />} items={menuItems} />
        </div>
      </div>
      {expanded && <Detail model={model} canEdit={canEdit} onConfigure={onConfigure} onReport={onReport} id={detailId} />}
    </div>
  );
}
